import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import LazyImage from '../../../components/LazyImage';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';

const ActivityFeed = ({ milestones, workoutActivity, userId }) => {
  const getMilestoneIcon = (type) => {
    const icons = {
      streak: 'Flame',
      workout: 'Dumbbell',
      nutrition: 'Apple',
      sleep: 'Moon',
      challenge: 'Trophy',
      badge: 'Award',
      duel: 'Swords',
      challenge_completed: 'Trophy',
      streak_milestone: 'Flame',
      badge_earned: 'Award',
      goal_achieved: 'Target',
      default: 'Star'
    };
    return icons?.[type] || icons?.default;
  };

  const getMilestoneColor = (type) => {
    const colors = {
      streak: 'text-warning',
      workout: 'text-primary',
      nutrition: 'text-success',
      sleep: 'text-accent',
      challenge: 'text-warning',
      badge: 'text-primary',
      duel: 'text-error',
      challenge_completed: 'text-warning',
      streak_milestone: 'text-warning',
      badge_earned: 'text-primary',
      goal_achieved: 'text-success',
      default: 'text-foreground'
    };
    return colors?.[type] || colors?.default;
  };

  const getWorkoutIcon = (workoutType) => {
    const icons = {
      strength: 'Dumbbell',
      cardio: 'Activity',
      running: 'Footprints',
      cycling: 'Bike',
      swimming: 'Waves',
      yoga: 'Wind',
      hiit: 'Zap',
      walking: 'PersonStanding',
      default: 'Dumbbell'
    };
    return icons?.[workoutType?.toLowerCase()] || icons?.default;
  };

  const getIntensityBadge = (level) => {
    const config = {
      low: { label: 'Baja', color: 'bg-success/10 text-success' },
      moderate: { label: 'Moderada', color: 'bg-warning/10 text-warning' },
      high: { label: 'Alta', color: 'bg-error/10 text-error' },
      extreme: { label: 'Extrema', color: 'bg-purple-500/10 text-purple-500' }
    };
    return config?.[level] || null;
  };

  // Merge and sort milestones + workout activity by date
  const combinedFeed = useMemo(() => {
    const milestoneItems = (milestones || [])?.map(m => ({
      ...m,
      _feedType: 'milestone',
      _sortDate: m?.created_at
    }));

    const workoutItems = (workoutActivity || [])?.map(w => ({
      ...w,
      _feedType: 'workout',
      _sortDate: w?.createdAt
    }));

    return [...milestoneItems, ...workoutItems]?.sort((a, b) => new Date(b._sortDate) - new Date(a._sortDate))?.slice(0, 25);
  }, [milestones, workoutActivity]);

  if (combinedFeed?.length === 0) {
    return (
      <div className="text-center py-12 bg-muted/30 rounded-xl">
        <Icon name="Activity" size={48} className="text-muted-foreground mx-auto mb-4" />
        <p className="text-muted-foreground">No hay actividad reciente</p>
        <p className="text-sm text-muted-foreground mt-2">Los logros y entrenamientos de tus amigos aparecerán aquí</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {combinedFeed?.map((item, index) => {
        if (item?._feedType === 'workout') {
          // Render workout activity card
          const isOwn = item?.userId === userId;
          const timeAgo = item?.createdAt
            ? formatDistanceToNow(new Date(item.createdAt), { addSuffix: true, locale: es })
            : '';
          const intensityBadge = getIntensityBadge(item?.intensityLevel);

          return (
            <motion.div
              key={`workout-${item?.id}`}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.04 }}
              className={`p-4 rounded-xl border-2 transition-all ${
                isOwn ? 'bg-primary/5 border-primary/30' : 'bg-card border-border hover:border-primary/30'
              }`}
            >
              <div className="flex items-start space-x-4">
                {/* Avatar */}
                <div className="flex-shrink-0">
                  {item?.user?.avatar_url ? (
                    <LazyImage
                      src={item?.user?.avatar_url}
                      alt={item?.user?.full_name || 'User avatar'}
                      className="w-12 h-12 rounded-full"
                      sizes="48px"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-lg font-semibold text-primary">
                        {item?.user?.full_name?.charAt(0)?.toUpperCase()}
                      </span>
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2 mb-1">
                    <p className="font-semibold text-foreground">
                      {isOwn ? 'Tú' : item?.user?.full_name}
                    </p>
                    <span className="text-muted-foreground">•</span>
                    <p className="text-sm text-muted-foreground">{timeAgo}</p>
                  </div>

                  <div className="flex items-start space-x-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Icon name={getWorkoutIcon(item?.workoutType)} size={20} className="text-primary" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-foreground mb-1">
                        {item?.exerciseName || item?.workoutType || 'Entrenamiento'}
                      </h4>
                      <div className="flex flex-wrap items-center gap-2 mt-1">
                        {item?.durationMinutes > 0 && (
                          <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                            <Icon name="Clock" size={11} />
                            {item?.durationMinutes} min
                          </span>
                        )}
                        {item?.caloriesBurned > 0 && (
                          <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                            <Icon name="Flame" size={11} className="text-warning" />
                            {item?.caloriesBurned} kcal
                          </span>
                        )}
                        {item?.steps > 0 && (
                          <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                            <Icon name="Footprints" size={11} />
                            {item?.steps?.toLocaleString()} pasos
                          </span>
                        )}
                        {intensityBadge && (
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${intensityBadge?.color}`}>
                            {intensityBadge?.label}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          );
        }

        // Render milestone card
        const isOwnMilestone = item?.user_id === userId;
        const timeAgo = item?.created_at
          ? formatDistanceToNow(new Date(item.created_at), { addSuffix: true, locale: es })
          : '';

        return (
          <motion.div
            key={`milestone-${item?.id}`}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.04 }}
            className={`p-4 rounded-xl border-2 transition-all ${
              isOwnMilestone
                ? 'bg-primary/5 border-primary/30' :'bg-card border-border hover:border-primary/30'
            }`}
          >
            <div className="flex items-start space-x-4">
              {/* User Avatar */}
              <div className="flex-shrink-0">
                {item?.user_profiles?.avatar_url ? (
                  <LazyImage
                    src={item?.user_profiles?.avatar_url}
                    alt={item?.user_profiles?.full_name || 'User avatar'}
                    className="w-12 h-12 rounded-full"
                    sizes="48px"
                  />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                    <span className="text-lg font-semibold text-primary">
                      {item?.user_profiles?.full_name?.charAt(0)?.toUpperCase()}
                    </span>
                  </div>
                )}
              </div>

              {/* Activity Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center space-x-2 mb-1">
                  <p className="font-semibold text-foreground">
                    {isOwnMilestone ? 'Tú' : item?.user_profiles?.full_name}
                  </p>
                  <span className="text-muted-foreground">•</span>
                  <p className="text-sm text-muted-foreground">{timeAgo}</p>
                </div>

                <div className="flex items-start space-x-3">
                  <div className={`w-10 h-10 rounded-full bg-${item?.color || 'primary'}/10 flex items-center justify-center flex-shrink-0`}>
                    <Icon
                      name={getMilestoneIcon(item?.milestone_type)}
                      size={20}
                      className={getMilestoneColor(item?.milestone_type)}
                    />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-foreground mb-1">{item?.title}</h4>
                    <p className="text-sm text-muted-foreground">{item?.description}</p>
                    {item?.points_earned > 0 && (
                      <div className="mt-2 inline-flex items-center space-x-1 px-2 py-1 bg-primary/10 rounded-full">
                        <Icon name="Plus" size={12} className="text-primary" />
                        <span className="text-xs font-semibold text-primary">
                          {item?.points_earned} puntos
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        );
      })}
    </div>
  );
};

export default ActivityFeed;